/**
 * @fileoverview Report export handlers extracted from the Reports page.
 * @module pages/reports/reportDownloads
 */
import { toast } from "react-toastify";
import { logger, sanitizeError } from "../../utils/logger.js";
import { getApiErrorMessage } from "../../utils/apiErrorMessages.js";
import {
  exportAuthenticationLogs,
  exportBuyerLogs,
  exportB2bEventAttendanceLogs,
  exportInvoiceLogs,
  exportGeneralReportOnBusinessEventsBuyerCompaniesAppointments,
  exportBusinessEventAppointmentDetailsReport,
  exportBusinessEventRequirementsReport,
  exportAccumulatedEventsConnectionsReport,
  exportEventsAttendancesReport,
  exportSACReport,
  exportGeneralB2BReport,
  exportVirtualEventsRegistrationReport,
} from "../../logic/reports";
import {
  APPOINTMENT_DETAIL_REPORT_PREFIX,
  ATTENDANCES_REPORT_PREFIX,
  ACCUMULATED_CONNECTIONS_REPORT_PREFIX,
  GENERAL_B2B_REPORT_PREFIX,
  REQUIREMENTS_REPORT_PREFIX,
  SAC_REPORT_PREFIX,
  VIRTUAL_EVENT_REGISTRATION_REPORT_PREFIX,
} from "../../utils/reportConstants.js";
import {
  buildEventReportFilename,
  buildTimestampedReportFilename,
} from "../../utils/reportHelpers.js";
import {
  notifyReportExportError,
  runReportExport,
  startReportDownload,
} from "./reportExportUtils.js";

const selectedBuyerInformation = (selectedBuyerCompanies) => {
  if (selectedBuyerCompanies?.length > 0) {
    return {
      type: "selected",
      selectedIds: selectedBuyerCompanies.map((company) => company._id),
    };
  }
  return {};
};

export const downloadAuthenticationLogs = async (ctx) =>
  runReportExport({
    isValid: () =>
      Boolean(ctx.selectedMemberCompany || ctx.startDate || ctx.endDate),
    missingFieldsMessage:
      "Error exporting authentication logs: Missing required fields",
    exportFn: () =>
      exportAuthenticationLogs(
        ctx.selectedMemberCompany?._id,
        ctx.startDate,
        ctx.endDate,
        ctx.navigate,
      ),
    filename: "AuthenticationLogs.xlsx",
    errorFallback: "FORBIDDEN",
    setLoadingExportLogs: ctx.setLoadingExportLogs,
    toastIdRef: ctx.toastId,
    failureLogMessage: "Error exporting authentication logs",
  });

export const downloadBuyerLogs = async (ctx) =>
  runReportExport({
    isValid: () =>
      Boolean(ctx.selectedEvent || ctx.selectedBuyerCompanies?.length > 0),
    missingFieldsMessage: "Error exporting buyer logs: Missing required fields",
    exportFn: () =>
      exportBuyerLogs(
        ctx.selectedEvent?._id,
        selectedBuyerInformation(ctx.selectedBuyerCompanies),
        ctx.navigate,
      ),
    filename: "BuyerLogs.xlsx",
    errorFallback: "FORBIDDEN",
    setLoadingExportLogs: ctx.setLoadingExportLogs,
    toastIdRef: ctx.toastId,
    failureLogMessage: "Error exporting buyer logs",
  });

export const downloadB2bEventAttendanceLogs = async (ctx) => {
  if (!ctx.selectedB2bEvent) {
    const errorType = "NO_EVENT_SELECTED";
    ctx.toastId.current = errorType;
    toast.error(getApiErrorMessage({ errorType }), {
      toastId: ctx.toastId.current,
    });
    return;
  }

  await runReportExport({
    isValid: () => true,
    missingFieldsMessage: "",
    exportFn: () =>
      exportB2bEventAttendanceLogs(
        ctx.selectedB2bEvent?._id,
        selectedBuyerInformation(ctx.selectedB2bBuyerCompanies),
        ctx.selectedB2bBuyerUser?._id,
        ctx.navigate,
      ),
    filename: "B2bEventAttendanceLogs.xlsx",
    errorFallback: "ERROR_EXPORTING_B2B_EVENT_ATTENDANCE_LOGS",
    setLoadingExportLogs: ctx.setLoadingExportLogs,
    toastIdRef: ctx.toastId,
    failureLogMessage: "Error exporting b2b event attendance logs",
  });
};

export const downloadInvoiceLogs = async (ctx, invoiceType) =>
  runReportExport({
    isValid: () => Boolean(ctx.selectedYear && ctx.selectedEvent),
    missingFieldsMessage: "Error exporting invoice logs: Missing required fields",
    exportFn: () =>
      exportInvoiceLogs(
        ctx.selectedYear,
        ctx.selectedEvent?._id,
        ctx.billed,
        invoiceType,
        ctx.navigate,
      ),
    filename: "InvoiceLogs.xlsx",
    errorFallback: "ERROR_EXPORTING_INVOICE_LOGS",
    setLoadingExportLogs: ctx.setLoadingExportLogs,
    toastIdRef: ctx.toastId,
    failureLogMessage: "Error exporting invoice logs",
  });

export const downloadBusinessEventAppointmentDetailsReport = async (ctx) =>
  runReportExport({
    isValid: () => Boolean(ctx.selectedYear && ctx.selectedEvent),
    missingFieldsMessage:
      "Error exporting business event appointment details report: Missing required fields",
    exportFn: () =>
      exportBusinessEventAppointmentDetailsReport(
        ctx.selectedEvent?._id,
        ctx.navigate,
      ),
    filename: buildEventReportFilename(
      APPOINTMENT_DETAIL_REPORT_PREFIX,
      ctx.selectedEvent?.nombre,
    ),
    errorFallback: "ERROR_EXPORTING_BUSINESS_EVENT_APPOINTMENT_DETAILS_REPORT",
    setLoadingExportLogs: ctx.setLoadingExportLogs,
    toastIdRef: ctx.toastId,
    failureLogMessage:
      "Error exporting business event appointment details report",
  });

export const downloadBusinessEventRequirementsReport = async (ctx) =>
  runReportExport({
    isValid: () => Boolean(ctx.selectedYear && ctx.selectedEvent),
    missingFieldsMessage:
      "Error exporting business event requirements report: Missing required fields",
    exportFn: () =>
      exportBusinessEventRequirementsReport(
        ctx.selectedEvent?._id,
        ctx.navigate,
      ),
    filename: buildEventReportFilename(
      REQUIREMENTS_REPORT_PREFIX,
      ctx.selectedEvent?.nombre,
    ),
    errorFallback: "ERROR_EXPORTING_BUSINESS_EVENT_REQUIREMENTS_REPORT",
    setLoadingExportLogs: ctx.setLoadingExportLogs,
    toastIdRef: ctx.toastId,
    failureLogMessage: "Error exporting business event requirements report",
  });

export const downloadAccumulatedEventsConnectionsReport = async (ctx) =>
  runReportExport({
    isValid: () => Boolean(ctx.selectedYear && ctx.selectedEventTypes),
    missingFieldsMessage:
      "Error exporting accumulated events connections report: Missing required fields",
    exportFn: () =>
      exportAccumulatedEventsConnectionsReport(
        ctx.selectedYear,
        ctx.selectedEventTypes.map((type) => type.value),
        ctx.navigate,
      ),
    filename: buildTimestampedReportFilename(
      ACCUMULATED_CONNECTIONS_REPORT_PREFIX,
    ),
    errorFallback: "ERROR_EXPORTING_ACCUMULATED_EVENTS_CONNECTIONS_REPORT",
    setLoadingExportLogs: ctx.setLoadingExportLogs,
    toastIdRef: ctx.toastId,
    failureLogMessage: "Error exporting accumulated events connections report",
  });

export const downloadGeneralReportOnBusinessEventsBuyerCompaniesAppointments =
  async (ctx) =>
    runReportExport({
      isValid: () => Boolean(ctx.selectedYear && ctx.selectedEvent),
      missingFieldsMessage:
        "Error exporting general report on business events buyer companies appointments: Missing required fields",
      exportFn: () =>
        exportGeneralReportOnBusinessEventsBuyerCompaniesAppointments(
          ctx.selectedEvent?._id,
          ctx.navigate,
        ),
      filename: "GeneralReportOnBusinessEventsBuyerCompaniesAppointments.xlsx",
      errorFallback:
        "ERROR_EXPORTING_GENERAL_REPORT_ON_BUSINESS_EVENTS_BUYER_COMPANIES_APPOINTMENTS",
      setLoadingExportLogs: ctx.setLoadingExportLogs,
      toastIdRef: ctx.toastId,
      failureLogMessage:
        "Error exporting general report on business events buyer companies appointments",
    });

export const downloadEventsAttendancesReport = async (ctx) =>
  runReportExport({
    isValid: () => Boolean(ctx.selectedYear && ctx.selectedEventTypes),
    missingFieldsMessage:
      "Error exporting events attendances report: Missing required fields",
    exportFn: () =>
      exportEventsAttendancesReport(
        ctx.selectedYear,
        ctx.selectedEventTypes.map((type) => type.value),
        ctx.navigate,
      ),
    filename: buildTimestampedReportFilename(ATTENDANCES_REPORT_PREFIX),
    errorFallback: "ERROR_EXPORTING_EVENTS_ATTENDANCES_REPORT",
    setLoadingExportLogs: ctx.setLoadingExportLogs,
    toastIdRef: ctx.toastId,
    failureLogMessage: "Error exporting events attendances report",
  });

export const downloadSACReport = async (ctx) => {
  try {
    ctx.setLoadingExportLogs(true);
    const s3Link = await exportSACReport(ctx.navigate);
    if (
      !startReportDownload(
        s3Link,
        buildTimestampedReportFilename(SAC_REPORT_PREFIX),
      )
    ) {
      logger.error("Error exporting members companies quantities report");
    }
  } catch (error) {
    notifyReportExportError(
      error,
      "ERROR_EXPORTING_MEMBERS_COMPANIES_QUANTITIES_REPORT",
      ctx.toastId,
    );
    logger.error("Error", { err: sanitizeError(error) });
  } finally {
    ctx.setLoadingExportLogs(false);
  }
};

export const downloadGeneralB2BReport = async (ctx) =>
  runReportExport({
    isValid: () => Boolean(ctx.selectedYear && ctx.selectedB2bEvent),
    missingFieldsMessage:
      "Error exporting general B2B report: Missing required fields",
    exportFn: () =>
      exportGeneralB2BReport(ctx.selectedB2bEvent?._id, ctx.navigate),
    filename: buildEventReportFilename(
      GENERAL_B2B_REPORT_PREFIX,
      ctx.selectedB2bEvent?.nombre,
    ),
    errorFallback: "ERROR_EXPORTING_GENERAL_B2B_REPORT",
    setLoadingExportLogs: ctx.setLoadingExportLogs,
    toastIdRef: ctx.toastId,
    failureLogMessage: "Error exporting general B2B report",
  });

export const downloadVirtualEventsRegistrationReport = async (ctx) =>
  runReportExport({
    isValid: () => Boolean(ctx.selectedYear && ctx.selectedEvent),
    missingFieldsMessage:
      "Error exporting virtual events registration report: Missing required fields",
    exportFn: () =>
      exportVirtualEventsRegistrationReport(
        ctx.selectedEvent?._id,
        ctx.navigate,
      ),
    filename: buildEventReportFilename(
      VIRTUAL_EVENT_REGISTRATION_REPORT_PREFIX,
      ctx.selectedEvent?.nombre,
    ),
    errorFallback: "ERROR_EXPORTING_VIRTUAL_EVENTS_REGISTRATION_REPORT",
    setLoadingExportLogs: ctx.setLoadingExportLogs,
    toastIdRef: ctx.toastId,
    failureLogMessage: "Error exporting virtual events registration report",
  });
